import React from 'react';
import { useTranslation } from 'next-i18next';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import Box from '@mui/material/Box';

import Typography from 'src/components/ui/Typography';
import TextField from 'src/components/ui/TextField';
import { styledComponent } from 'src/utils/styled';
import Checkbox from 'src/components/ui/Checkbox';
import { colours } from 'src/constants/colours';
import { useLegalLinksQuery } from 'src/hooks/__generated__/queries';
import Button from 'src/components/ui/Button';

const StyledTextField = styledComponent(TextField)`
    margin-bottom: 0rem;
`;

const ConditionsText = styledComponent(Typography)`
    margin-bottom: 2rem;
    a {
        color: ${colours.secondary.main};
        text-decoration: none;
        transition: text-decoration 0.3s ease;
        &:hover {
          text-decoration: underline;
        }
    }
`;

export type QuickSignupFormValues = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    hasOptedInToMarketingComms: boolean;
};

type PrefilledInfos = {
    firstName?: string;
    lastName?: string;
    email?: string;
};

type Props = {
    prefilledInfos?: PrefilledInfos;
    onSubmit: (values: QuickSignupFormValues) => void;
};

export const QuickSignupForm: React.FC<Props> = ({ prefilledInfos, onSubmit }) => {
    const { t } = useTranslation(['common']);
    const { data: links } = useLegalLinksQuery();

    const schema = Yup.object().shape({
        email: Yup.string().email(t('common:errors.invalidEmail')).required(t('common:errors.required')),
        firstName: Yup.string()
            .nullable()
            .required(t('common:errors.required'))
            .max(255, t('common:errors.tooLong', { number: 255 })),
        lastName: Yup.string()
            .nullable()
            .required(t('common:errors.required'))
            .max(255, t('common:errors.tooLong', { number: 255 })),
        password: Yup.string()
            .required(t('common:errors.required'))
            .min(8, t('common:errors.tooShort', { number: 8 })),
    });

    return (
        <Formik
            initialValues={{
                lastName: prefilledInfos?.lastName ?? '',
                firstName: prefilledInfos?.firstName ?? '',
                email: prefilledInfos?.email ?? '',
                password: '',
                hasOptedInToMarketingComms: false,
            }}
            validationSchema={schema}
            onSubmit={onSubmit}
        >
            {({ isValid, isSubmitting }) => (
                <Form>
                    <Box>
                        <Box display="flex" gap="1rem">
                            <StyledTextField
                                name="lastName"
                                label={t('common:signUpProStep2.lastName')}
                                InputLabelProps={{ required: true }}
                            />
                            <StyledTextField
                                name="firstName"
                                label={t('common:signUpProStep2.firstName')}
                                InputLabelProps={{ required: true }}
                            />
                        </Box>
                        <StyledTextField
                            name="email"
                            label={t('common:signUp.email')}
                            InputLabelProps={{ required: true }}
                        />
                        <StyledTextField
                            name="password"
                            label={t('common:signUp.password')}
                            type="password"
                            helperText={t('common:signUp.passwordHelper')}
                            InputLabelProps={{ required: true }}
                        />
                        <Box marginTop="1rem">
                            <Checkbox
                                name="hasOptedInToMarketingComms"
                                label={t('common:signUp.hasOptedInToMarketingComms')}
                            />
                        </Box>
                        <ConditionsText variant="subtitle2">
                            <span
                                data-target="signup-checkbox"
                                data-target-id="signup-checkbox"
                                dangerouslySetInnerHTML={{
                                    __html: t('common:signUp.policyConditions', {
                                        generalConditionsLink: links?.generalConditionsPro,
                                        privacyPolicyLink: links?.privacyPolicy,
                                        cookiePolicyLink: links?.cookiePolicy,
                                    }),
                                }}
                            />
                        </ConditionsText>
                        <Button type="submit" disabled={!isValid || isSubmitting} loading={isSubmitting} fakeDisabled>
                            {t('common:signUp.signUp')}
                        </Button>
                    </Box>
                </Form>
            )}
        </Formik>
    );
};
